import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ASSISTANT_ENDPOINT_PATH, assistantApi, threadsApi } from '@/services/api';
import { useOptionalRefreshToken } from '@/contexts/AuthContext';
import { trackEvent, currentLocationTag } from '@/lib/analytics';
import { toComposerError, hasFriendlyMessage } from '@/utils/composerErrors';
import { assistantTargetFields } from '@/utils/assistantTargetSummary';
import { useEndpoint } from '@/hooks/useEndpoints';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useGeneratedUis } from '@/hooks/useGeneratedUis';
import { criteriaQueryKey } from '@/hooks/useEndpointTestEvals';
import { useActiveMcpConnectionSummaries } from '@/hooks/useMcpConnections';
import {
  isKnowledgeDocumentSettling,
  useKnowledgeBaseSummaries,
  useKnowledgeDocument,
  useTargetAgentKnowledge,
} from '@/hooks/useKnowledge';
import config from '@/config';
import { replyFromTurnOutput, replyText } from '@/types/assistant';
import type { ArtifactReference } from '@/types/artifacts';
import type { AssistantPresentation } from '@/components/layout/AssistantSidebar';
import type {
  AssistantAttachment,
  AssistantAvailableConnection,
  AssistantAvailableKnowledgeBase,
  AssistantContextEnvelope,
  AssistantReplyEnvelope,
  AssistantSpecialist,
  AssistantThreadMessage,
  AssistantThreadSummary,
  AssistantTurn,
} from '@/types/assistant';

/**
 * Assistant conversation state (task 720).
 *
 * Lives ABOVE the routes so navigation never drops the conversation — describe
 * a service on the Dashboard, approve the build, follow the result link to the
 * new service's page, and the thread is still there. That is the single most
 * important property of the surface, and it is why the state is a provider
 * rather than component state inside the sidebar: the docked panel, the tablet
 * drawer and the mobile sheet are three presentations of ONE conversation.
 *
 * What it owns:
 *
 *   1. **The thread.** `threadId` addresses the durable conversation; the
 *      client never sees or holds a `collaborationId`, because which execution
 *      carries the next turn is the platform's business (a failed run, a
 *      redeploy, or the dispatcher switching specialists all re-back a thread).
 *   2. **The context envelope.** Every turn carries where the User is —
 *      `location` from the analytics route tag, plus `resourceType`/`resourceId`
 *      on any resource detail page (task 746). Entity context is a routing
 *      input, not decoration: it is what lets "generate a UI for this" resolve
 *      to the right service, and it is what files the thread under the resource
 *      so returning to the page returns to the conversation.
 *   3. **The action lifecycle.** Propose → approve → apply, tracked per turn.
 *      Nothing mutates the account before the User approves the card in the
 *      conversation.
 *   4. **Open/closed and unread.** The container state the three presentations
 *      share, so a reply that lands while the panel is collapsed leaves a mark.
 */

/**
 * Route → resource, the single seam for conversation scope (task 746).
 *
 * Every resource DETAIL route contributes a `{ resourceType, resourceId }` pair;
 * everything else — lists, the Dashboard, Settings, the Composer — is *general*,
 * and its conversations belong to no resource. This table is the only place that
 * mapping lives, so a new detail page joins the scheme by adding one row here
 * rather than by changing the envelope, the listing filters, or the schema.
 *
 * `resourceType` deliberately reuses the service's own resource vocabulary
 * (`endpoint`, `collaboration-config`, …) rather than inventing app-side names:
 * the string is stored on the thread's resource link and matched verbatim by the
 * listing filter, so a divergence here would file conversations under a resource
 * type nothing else in the platform can name.
 */
const RESOURCE_ROUTES: { pattern: RegExp; resourceType: string }[] = [
  // AI Services answer on two paths — `/services/:id` is the product label,
  // `/endpoints/:id` the original route — and both mean the same resource.
  { pattern: /^\/services\/([^/]+)/, resourceType: 'endpoint' },
  { pattern: /^\/endpoints\/([^/]+)/, resourceType: 'endpoint' },
  // The Advanced menu.
  { pattern: /^\/configs\/([^/]+)/, resourceType: 'collaboration-config' },
  { pattern: /^\/agents\/([^/]+)/, resourceType: 'agent' },
  { pattern: /^\/prompts\/([^/]+)/, resourceType: 'prompt' },
  { pattern: /^\/message-routes\/([^/]+)/, resourceType: 'message-route' },
  // The Settings section of the user menu.
  { pattern: /^\/model-configs\/([^/]+)/, resourceType: 'model-config' },
  { pattern: /^\/mcp-connections\/([^/]+)/, resourceType: 'mcp-connection' },
];

/** `/x/new` is the CREATE form: a page about a resource that does not exist yet. */
const NON_ID_SEGMENTS = new Set(['new']);

/**
 * The resource the given path is about, or null for a general page.
 *
 * Nested detail routes resolve to their PARENT resource — `/endpoints/:id/runs/
 * :collaborationId` is still a conversation about that AI Service — because the
 * first path segment pair is what the patterns match.
 */
export function resourceFromPath(
  pathname: string
): { resourceType: string; resourceId: string } | null {
  for (const { pattern, resourceType } of RESOURCE_ROUTES) {
    const match = pattern.exec(pathname);
    if (match && !NON_ID_SEGMENTS.has(match[1])) {
      return { resourceType, resourceId: match[1] };
    }
  }
  return null;
}

/**
 * WHERE A FINISHED TURN LEAVES THE USER (task 871).
 *
 * Two rules, both here because the provider is the only party that sees every
 * completed turn and the current route at the same moment. Both are strictly
 * about NAVIGATION: neither changes what the turn did, and neither can fail in
 * a way the User has to deal with.
 *
 *   1. **The build landing.** A Composer build that succeeds while the User is
 *      on the Dashboard lands them on the new AI Service's page — everything
 *      the conversation is heading towards next (refining, testing, exposing
 *      it) lives there. The conversation comes along without a call of its own:
 *      the service re-files a general thread under what it built (the `created`
 *      link in `innovate-service/src/processes/thread.ts`), so the endpoint-
 *      scoped listing the resolution effect makes on arrival returns that same
 *      thread — and the Dashboard's general listing stops returning it. One
 *      thread, one home, decided by the party that owns the link.
 *
 *      The New AI Service page (task 1117) and the AI Services list (task
 *      1264) land the same way: with the Dashboard they are the three places a
 *      User starts a service.
 *
 *      From any OTHER route nothing moves. A User who walked away mid-compose
 *      is doing something else, and the ready card's "Open service" link is the
 *      affordance there.
 *
 *   2. **The tab a result lives on.** Asking for an interface and being left on
 *      Overview to go and find it is the whole complaint. When a turn saves
 *      something that has a home on the AI Service in view, that tab opens.
 *
 * Rule 1 wins when a turn could somehow satisfy both: a build turn's landing is
 * the service itself, and the tabs are for the turns that follow it.
 */
const DASHBOARD_PATH = '/dashboard';

/**
 * The Dashboard, and only it. `/` is a redirect-only route (`HomeRoute` sends it
 * here when `innovate.fe.dashboard.page` is on), so a Dashboard User's pathname
 * is always the canonical one by the time a turn completes.
 */
const isDashboardPath = (pathname: string): boolean =>
  pathname === DASHBOARD_PATH || pathname === `${DASHBOARD_PATH}/`;

/**
 * THE AI SERVICES LIST, the build landing's third origin (task 1264).
 *
 * Exactly the list: `/services/:id` is a service page (a build there is a
 * modify, and the User is already where it lands) and `/services/new` is the
 * New AI Service page, which has its own rule. The list is one of the three
 * places a User starts a service, so it lands like the Dashboard does.
 */
const SERVICES_LIST_PATH = '/services';

const isServicesListPath = (pathname: string): boolean =>
  pathname === SERVICES_LIST_PATH || pathname === `${SERVICES_LIST_PATH}/`;

/**
 * THE NEW AI SERVICE PAGE IS A PRESENTATION OF THIS CONVERSATION (task 1117).
 *
 * `/services/new` renders the Assistant conversation full width in the page
 * body, so the provider reports the `page` presentation there and the docked
 * sidebar shows only its rail — one conversation, never shown twice. Selected
 * by ROUTE, unlike the other three, which the viewport width decides.
 *
 * It is also the build landing's second origin (rule 1 above): a User who
 * designed a service here has nothing left to do on the page once it is built.
 */
export const ASSISTANT_PAGE_PATH = '/services/new';

export const isAssistantPagePath = (pathname: string): boolean =>
  pathname === ASSISTANT_PAGE_PATH || pathname === `${ASSISTANT_PAGE_PATH}/`;

/**
 * The AI Service whose DETAIL PAGE this path is, or null.
 *
 * Deliberately the two-segment path only: `/services/:id/runs/:collaborationId`
 * is its own page with no tab row, and `/endpoints/:id` is the older hub, whose
 * tabs are a different set. Selecting a tab that does not exist on the page in
 * front of the User would land them on Overview by fallback — worse than
 * leaving them where they are.
 */
const serviceDetailIdFromPath = (pathname: string): string | null => {
  const match = /^\/services\/([^/]+)\/?$/.exec(pathname);
  return match && !NON_ID_SEGMENTS.has(match[1]) ? match[1] : null;
};

/**
 * Which specialist's saved result lives on which tab of the AI Service page —
 * `?tab=` values from `src/pages/ServiceDetailPage.tsx`.
 *
 * A table rather than a chain of ifs so a future tier joins by adding one row.
 * Keyed on the answering specialist because that is what the turn already
 * carries; the accompanying `savedArtifact.saved` is the "it actually landed"
 * half, checked once below rather than per row.
 */
const RESULT_TABS: Partial<Record<AssistantSpecialist, 'interface' | 'testing'>> = {
  'ui-composer': 'interface',
  'ui-composer-revise': 'interface',
  'test-composer': 'testing',
  'criteria-composer': 'testing',
};

/**
 * The section of that tab, where the tab has sections (task 1188): the Testing
 * tab's `?section=` (task 1185). Saved criteria land on Criteria; saved tests
 * on Tests. A tab without sections ignores the param.
 */
const RESULT_SECTIONS: Partial<Record<AssistantSpecialist, string>> = {
  'test-composer': 'tests',
  'criteria-composer': 'criteria',
};

/**
 * WHICH CACHE A SAVED RESULT INVALIDATES (task 876).
 *
 * The UI and test tiers persist INSIDE the turn (the service's P9 save steps),
 * so the client never runs a save mutation and none of the `onSuccess`
 * invalidations in `src/hooks/*` fire. The tab the result lives on therefore
 * kept rendering its pre-turn cache: with the User already on it, the
 * replace-navigate above is a no-op, nothing unmounts, and the rail, the header
 * and the preview all stayed on the superseded version — while the turn's card
 * said the save succeeded.
 *
 * The query-key root each tab reads, keyed by the specialist that saved, so a
 * future tier joins by adding one row here and one above. Keys are always
 * `[root, serviceId]` — see `useGeneratedUis` and `useEndpointTests`.
 */
const RESULT_QUERY_ROOTS: Partial<Record<AssistantSpecialist, string>> = {
  'ui-composer': 'generated-endpoint-uis',
  'ui-composer-revise': 'generated-endpoint-uis',
  'test-composer': 'endpoint-tests',
};

/**
 * Tiers whose tab reads a key that is not `[root, serviceId]` (task 1188): the
 * Criteria section reads the service-scope criteria key from
 * `useEndpointTestEvals`.
 */
const RESULT_QUERY_KEYS: Partial<Record<AssistantSpecialist, (serviceId: string) => readonly unknown[]>> = {
  'criteria-composer': (serviceId) => criteriaQueryKey({ kind: 'service', endpointId: serviceId }),
};

/**
 * Text waiting to be placed in the composer, not sent (task 1131). `nonce`
 * changes on every request, so asking twice with the same text still refills
 * and refocuses the input.
 */
export interface AssistantDraft {
  text: string;
  nonce: number;
}

interface AssistantContextValue {
  turns: AssistantTurn[];
  threadId: string | null;
  isSending: boolean;
  error: string | null;
  /** The page/entity envelope the next turn will carry. */
  context: AssistantContextEnvelope;

  /** Chat history — other threads, most recently active first. */
  threads: AssistantThreadSummary[];
  isLoadingThreads: boolean;

  /**
   * Send a turn. `startsNewThread` forces a fresh thread even when one is open;
   * a turn with no resolved thread already starts one (task 783).
   */
  send: (text: string, options?: { startsNewThread?: boolean }) => Promise<void>;
  /** Approve the action proposed by a turn, applying it as the User. */
  approveAction: (turnId: string) => Promise<void>;
  /**
   * The file the next turn will revise, if the User asked to revise one — the
   * artifact card's "Revise" raises it (task 828). Rendered beside the composer
   * so the pending base is visible before the turn goes.
   */
  revisingArtifact: ArtifactReference | null;
  /** Set (or clear, with `null`) that base. Cleared once the turn is sent. */
  reviseArtifact: (artifact: ArtifactReference | null) => void;

  /**
   * The document the conversation is working on, if the User attached one (task
   * 970). Unlike the revision base above it survives every turn until the User
   * takes it back: an attached spec is the subject of a conversation, not of one
   * message.
   */
  attachment: AssistantAttachment | null;
  /** Attach a freshly uploaded document; the provider polls it to Ready. */
  setAttachment: (attachment: AssistantAttachment) => void;
  /** Take the attachment back. The next turn carries no `documentId`. */
  clearAttachment: () => void;

  /** Start a fresh chat, keeping the old ones reachable in the history list. */
  newChat: () => void;
  /**
   * Start a NEW general thread whose first turn is `message` (task 1117) — the
   * New AI Service page's entry panel hands its description over through this.
   * Whatever conversation was open stays reachable in the history list.
   */
  seedFirstTurn: (message: string) => Promise<void>;
  /**
   * A draft for the composer (task 1131): placed in the input and focused, never
   * sent. The composer takes it once and calls `consumeDraft`, so typing after
   * that is the composer's own state and the draft cannot come back over it.
   */
  draft: AssistantDraft | null;
  /** Place `text` in the composer without sending it. */
  setDraft: (text: string) => void;
  /** Open the Assistant with `text` in the composer: `open()` then `setDraft(text)`. */
  openWithDraft: (text: string) => void;
  /** Called by the composer once it has taken the draft. */
  consumeDraft: () => void;
  /**
   * A request to pulse the composer (task 1196): a nonce, bumped by
   * `pulseComposer`, that the sidebar composer takes once (`consumeComposerPulse`)
   * and answers with a one-cycle ring. A page button that sends a turn for the
   * User (Suggest criteria) uses it to mark where the request landed. `null`
   * when there is nothing to take — the same take-once shape as `draft`, so a
   * composer that mounts after the request (a drawer opening) still pulses.
   */
  composerPulse: number | null;
  /** Ask the composer to pulse once. */
  pulseComposer: () => void;
  /** Called by the composer once it has taken the pulse. */
  consumeComposerPulse: () => void;
  /** Resume a stored thread, restoring its messages and cards. */
  resumeThread: (threadId: string) => Promise<void>;
  /** Rename a stored thread; its history row updates in place. */
  renameThread: (threadId: string, title: string) => Promise<void>;
  /** Archive a stored thread out of the history list (task 777). */
  archiveThread: (threadId: string) => Promise<void>;

  /**
   * Which presentation the conversation is in (task 1117): `page` on the New
   * AI Service page, otherwise the breakpoint's docked, drawer or sheet.
   */
  presentation: AssistantPresentation;

  /** Container state, shared by the docked, drawer and sheet presentations. */
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** A reply or completed action arrived while the panel was not showing. */
  hasUnread: boolean;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

/** Non-throwing access — surfaces outside the provider simply render nothing. */
export function useOptionalAssistant(): AssistantContextValue | undefined {
  return useContext(AssistantContext);
}

export function useAssistant(): AssistantContextValue {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
}

const newId = () => crypto.randomUUID();

/**
 * The key a conversation's context is filed under — one definition, so the
 * navigation effect and the post-send refresh can never disagree about which
 * context a response belongs to (task 776).
 */
const contextKeyOf = (resourceType?: string, resourceId?: string): string =>
  resourceType && resourceId ? `${resourceType}:${resourceId}` : 'general';

/**
 * Turn a stored transcript into the sidebar's rendered turns.
 *
 * The service stores a `user` turn as `{ message, context }` and an `innovate`
 * turn as the turn output verbatim — so the text lives under `message` for one
 * and inside the answering specialist's reply object for the other.
 * `replyFromTurnOutput` is the one place that asymmetry is resolved, and it is
 * the same read the live turn goes through (task 763), which is what keeps a
 * resumed conversation identical to the one that was just held. It also handles
 * turns stored before the per-node reshape, which are flat.
 *
 * This function is also the ONLY boundary between the two role vocabularies
 * (task 752): stored `user`/`innovate` on the way in, the app's own
 * `human`/`assistant` rendering roles on the way out. Nothing downstream of here
 * sees a stored role, which is why the service rename reached exactly one
 * mapping instead of every component that renders a turn.
 */
const turnsFromThread = (messages: AssistantThreadMessage[]): AssistantTurn[] =>
  messages.map((message) => {
    const fromUser = message.role === 'user';
    if (fromUser) {
      return {
        id: message.id,
        role: 'human' as const,
        text: message.content.message ?? '',
      };
    }

    const { specialist, reply } = replyFromTurnOutput(message.content);
    return {
      id: message.id,
      role: 'assistant' as const,
      text: replyText(reply),
      specialist,
      sources: reply?.sources,
      answered: reply?.answered,
      options: reply?.options,
      questions: reply?.questions,
      spec: reply?.spec,
      buildResult: reply?.buildResult,
      savedArtifact: reply?.savedArtifact,
      rawReply: reply?.response,
      scenarios: reply?.scenarios,
      artifacts: reply?.artifacts,
      // The connection the turn said was missing (task 962). Replayed like every
      // other field of a stored turn: the account may still not have it, and if
      // it does, the Connect action opens a form the User simply cancels.
      missingConnection: reply?.missingConnection,
      // The knowledge base the turn said was missing (task 1079), replayed for
      // the same reason as the connection above.
      missingKnowledgeBase: reply?.missingKnowledgeBase,
      // A stored proposal is settled history: it either produced a result or it
      // did not. Re-offering "Approve" on a resumed card would let one approval
      // apply twice.
      actionState: reply?.spec
        ? reply.buildResult
          ? reply.buildResult.status === 'succeeded'
            ? 'completed'
            : 'failed'
          : 'proposed'
        : undefined,
    };
  });

interface AssistantProviderProps {
  children: ReactNode;
  /** Start closed — the drawer/sheet presentations open on demand. */
  initiallyOpen?: boolean;
  /**
   * The viewport's presentation (docked, drawer or sheet), from
   * `useAssistantPresentation`. The route can override it with `page`.
   */
  presentation?: AssistantPresentation;
}

export function AssistantProvider({
  children,
  initiallyOpen = true,
  presentation: viewportPresentation = 'docked',
}: AssistantProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshToken = useOptionalRefreshToken();

  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);

  /**
   * THE ARTIFACT THE NEXT TURN REVISES (task 828).
   *
   * Held as state so the composer can show it, and mirrored to a ref because
   * `postTurn` reads it synchronously at send time — the same arrangement the
   * thread binding below uses, for the same reason. It survives exactly one
   * turn: revising is something the User asked for once, and a base that stuck
   * around would quietly re-version the same file on every later message.
   */
  const [revisingArtifact, setRevisingArtifact] = useState<ArtifactReference | null>(null);
  const revisingArtifactRef = useRef<ArtifactReference | null>(null);
  revisingArtifactRef.current = revisingArtifact;

  /**
   * The account's active connections, for the Composer guide (task 961).
   *
   * Held in a ref for the same reason the base above is: `postTurn` reads it
   * synchronously at send time, so a list that resolves while the person is
   * mid-conversation is picked up on the very next turn without re-creating the
   * callback. Unresolved or failed leaves it empty, and an empty list is not
   * sent at all — the guide's seeded fallback covers that case.
   */
  const { connections: availableConnections } = useActiveMcpConnectionSummaries();
  const availableConnectionsRef = useRef<AssistantAvailableConnection[]>([]);
  availableConnectionsRef.current = availableConnections;

  /**
   * The account's knowledge bases, for the Composer guide (task 1079). Same
   * arrangement as the connections above: read through a ref at send time, and
   * an unresolved or failed list leaves it empty, which is not sent at all.
   */
  const { knowledgeBases: availableKnowledgeBases } = useKnowledgeBaseSummaries();
  const availableKnowledgeBasesRef = useRef<AssistantAvailableKnowledgeBase[]>([]);
  availableKnowledgeBasesRef.current = availableKnowledgeBases;

  const reviseArtifact = useCallback((artifact: ArtifactReference | null) => {
    setRevisingArtifact(artifact);
  }, []);

  /**
   * THE DOCUMENT THE CONVERSATION IS WORKING ON (task 970).
   *
   * Two things make this different from the revision base above, and both follow
   * from how the service treats a subject document:
   *
   *   1. **It survives every turn.** The engine puts the id on the run context
   *      for the turn that carried it and never persists it, so a conversation
   *      that keeps referring to the file has to keep sending it. The chip above
   *      the composer is what makes that visible, and removing the chip — not
   *      sending a turn — is what ends it.
   *   2. **It has to be READY.** The service refuses a document that is still
   *      indexing, so the state carries the ingestion status and the composer
   *      holds Send on it.
   *
   * Mirrored to a ref for the same reason the two above are: `postTurn` reads it
   * synchronously at send time.
   */
  const [attachment, setAttachmentState] = useState<AssistantAttachment | null>(null);
  const attachmentRef = useRef<AssistantAttachment | null>(null);
  attachmentRef.current = attachment;

  /**
   * Whether this attachment has already been counted as a turn (task 970's
   * second analytics event). One event per attachment, not per turn: the
   * question the metric answers is how often an attached document is actually
   * used, and a five-turn conversation about one file is one use.
   */
  const attachmentTurnTrackedRef = useRef<string | null>(null);

  /**
   * Watch the attachment settle.
   *
   * The same hook the Documents library uses, so there is ONE poll interval and
   * ONE "still ingesting" predicate in the app rather than a second pair that
   * can drift: `useKnowledgeDocument` refetches every `KNOWLEDGE_STATUS_POLL_MS`
   * while `isKnowledgeDocumentSettling` holds and stops the timer when it does
   * not. Enabled only while this attachment is unsettled, so a Ready chip makes
   * no requests at all.
   */
  const { data: attachedDocument } = useKnowledgeDocument(
    attachment?.status === 'indexing' ? attachment.id : undefined
  );

  useEffect(() => {
    if (!attachedDocument) return;
    setAttachmentState((previous) => {
      // Guarded on the id: a poll answering for a document the User has already
      // removed — or replaced — must not write a status onto its successor.
      if (!previous || previous.id !== attachedDocument.id) return previous;
      if (isKnowledgeDocumentSettling(attachedDocument)) return previous;
      const settled = attachedDocument.status === 'ready' ? 'ready' : 'failed';
      return previous.status === settled ? previous : { ...previous, status: settled };
    });
  }, [attachedDocument]);

  const setAttachment = useCallback((next: AssistantAttachment) => {
    attachmentTurnTrackedRef.current = null;
    setAttachmentState(next);
  }, []);

  const clearAttachment = useCallback(() => {
    attachmentTurnTrackedRef.current = null;
    setAttachmentState(null);
  }, []);

  /**
   * THE RESOURCE THE OPEN CONVERSATION BELONGS TO (task 808).
   *
   * A thread is filed under at most one resource, decided when it is created and
   * never re-filed. That makes the resource conversation STATE, not a property
   * of whichever page the browser happens to be showing — and a held modify
   * conversation depends on it: the guide reads `resourceId` to know which
   * service it is changing.
   *
   * It used to be recomputed from the route on every turn, so a turn that left
   * here with no resource — which is what a context-free turn looks like — told
   * a mid-change conversation that its target had gone away. Live evidence: a
   * `{"message":"hello"}` turn mid-conversation got a fresh greeting and an
   * EMPTY spec back, wiping the change being assembled.
   *
   * So the resource that was in effect when this thread was opened is kept, and
   * every continued turn on that thread carries it. `null` means "not known" —
   * a brand-new chat before its first turn — and the route answers instead.
   */
  const threadResourceRef = useRef<{ resourceType?: string; resourceId?: string } | null>(null);
  /**
   * The same binding as state, so the target's current state can be LOADED for
   * it (task 813).
   *
   * The ref stays the authoritative store — `postTurn` reads it synchronously,
   * and every rule the tasks numbered 808 and 809 wrote around it is unchanged.
   * What a ref cannot do is drive a query: the summary a turn carries has to be
   * fetched for whichever service the conversation belongs to, and a fetch only
   * starts on a render. So both are written together, through `bindThreadResource`
   * below, and the ref is never assigned anywhere else.
   */
  const [threadResource, setThreadResource] = useState<{
    resourceType?: string;
    resourceId?: string;
  } | null>(null);
  /**
   * THE AI SERVICE THIS CONVERSATION BUILT (task 1264).
   *
   * A build that succeeds somewhere the landing does not move the User from
   * leaves the thread bound to no service — it was opened on a general page —
   * so the next turn ("yes, build an interface") would carry no `endpointId`
   * and the Interface save would refuse with "No AI Service in view". The app
   * already holds the built service's id, so it keeps it here and a continued
   * turn with no service of its own carries it instead.
   *
   * Keyed by the thread that built it: any other thread — New chat, a resumed
   * one, the one a navigation opens — reads it as absent, which is what clears
   * it when the thread changes.
   */
  const [builtService, setBuiltService] = useState<{
    threadId: string;
    endpointId: string;
  } | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState<AssistantThreadSummary[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [hasUnread, setHasUnread] = useState(false);

  /** 1-based question index within the conversation — the `turn` property of
   *  `help_assistant_asked`, whose contract this surface inherits unchanged. */
  const turnCountRef = useRef(0);
  /**
   * Bumped by `seedFirstTurn` (task 1117). A navigation-time resolution that was
   * still fetching when the New AI Service page started its conversation must
   * not then put the context's previous thread on screen over the new one — on
   * a fresh load of `/services/new` with a hand-off prompt the two race.
   */
  const seedGenerationRef = useRef(0);
  /**
   * The context whose conversation is actually ON SCREEN.
   *
   * It is a claim, and the claim is only kept by a run that DELIVERED (task
   * 809). It used to be claimed before the fetch and never released, which read
   * as "guards StrictMode's double effect" and was really the opposite: under
   * `React.StrictMode` (see `src/index.tsx`) mount effects are double-invoked,
   * so run 1 claimed the key and was immediately cleaned up — `cancelled` —
   * while run 2 saw the claim and returned without fetching. Run 1's listing
   * then arrived and was discarded by its own `cancelled` check, and the
   * sidebar sat empty on every FRESH PAGE LOAD. In-app navigation hid it,
   * because only mount effects are double-invoked, which is why the conversation
   * appeared as soon as the User moved around and never on a reload.
   */
  const resolvedContextRef = useRef<string | null>(null);
  /** Read inside async handlers, which must not close over a stale open state. */
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  /** Read by the navigation effect, which must see the LIVE sending state. */
  const isSendingRef = useRef(isSending);
  isSendingRef.current = isSending;
  /** Read inside `postTurn`, which must not be re-created on every listing. */
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  /**
   * The stream as it stands, read by `appendReply` — which is created once and
   * must not close over an empty conversation (task 998).
   */
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  /**
   * Read by the completion routing below, which asks where the User is AT THE
   * MOMENT THE REPLY LANDS — a turn can outlive several navigations, and the
   * route it was sent from is not the one it has to decide about.
   */
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const context = useMemo<AssistantContextEnvelope>(() => {
    const resource = resourceFromPath(location.pathname);
    return {
      location: currentLocationTag(),
      ...(resource ?? {}),
    };
  }, [location.pathname]);

  // Primitives, not the envelope object: the resolution effect must fire when
  // the User moves between RESOURCES, not on every route tag change (moving
  // between two tabs of the same AI Service is the same conversation).
  const { resourceType, resourceId } = context;

  /** File the open conversation under a resource — ref and state together. */
  const bindThreadResource = useCallback(
    (resource: { resourceType?: string; resourceId?: string } | null) => {
      threadResourceRef.current = resource;
      setThreadResource(resource);
    },
    []
  );

  /**
   * THE TARGET'S CURRENT STATE (task 813).
   *
   * Three tiers — modify, interface, test — work on an AI Service that already
   * exists and cannot look one up: the Composer guide holds no tools, and the
   * two generators map their `endpointSpec` off the turn. So the turn carries
   * the target's current state, and the app is the only party that can load it.
   *
   * Which service that is follows the CONVERSATION, not the page: a thread bound
   * to a service keeps it (the stickiness from the task numbered 808), and the
   * route answers only for a chat that has no binding yet. That is the same rule
   * `postTurn` applies to the resource pair itself, restated here because the
   * fetch has to start a render earlier than the send.
   *
   * Both reads are React Query, so on a service page they are cache hits on
   * queries the page has already made (`useEndpoint`, and `useGeneratedUis` via
   * the Interface section) rather than calls of their own. `assistantTargetFields`
   * then refuses to send a summary whose id does not match the resource the turn
   * carries, which is what keeps a stale fetch from describing the wrong service.
   */
  const builtServiceIdForThread =
    threadId && builtService?.threadId === threadId ? builtService.endpointId : undefined;
  const boundResource = threadId && threadResource ? threadResource : { resourceType, resourceId };
  // The built-service fallback (task 1264), applied here as well as in
  // `postTurn` so the summary is fetched for the service the turn will carry.
  const turnResource =
    builtServiceIdForThread && boundResource.resourceType !== 'endpoint'
      ? { resourceType: 'endpoint', resourceId: builtServiceIdForThread }
      : boundResource;
  const targetEndpointId =
    turnResource.resourceType === 'endpoint' ? turnResource.resourceId : undefined;
  const { data: targetEndpoint } = useEndpoint(targetEndpointId);
  const { data: targetGeneratedUis } = useGeneratedUis(targetEndpointId);
  // The target's Agents and the knowledge bases attached to each (task 1079),
  // so the modify guide can say what the service answers from today. Undefined
  // until every list has loaded, which leaves the key off the summary.
  const targetAgentKnowledge = useTargetAgentKnowledge(
    targetEndpoint && targetEndpoint.id === targetEndpointId
      ? targetEndpoint.collaborationConfigId
      : undefined
  );

  /**
   * Navigation-driven thread resolution (task 746).
   *
   * Conversations live where their subject lives, so which thread is open is a
   * function of where the User is: a resource page opens that resource's most
   * recently active thread, any other page opens the most recent GENERAL one.
   * Neither case is a login-time hydration any more — the same resolution runs
   * on first mount and on every later move between contexts.
   *
   * Two properties this deliberately keeps:
   *
   *   1. **An in-flight turn is never interrupted.** Navigating away while a
   *      reply is still coming leaves the conversation exactly where it is; the
   *      answer lands in its own thread, and resolution applies on the NEXT
   *      navigation. Switching mid-turn would strand the reply in a thread the
   *      User is no longer looking at.
   *   2. **Failure is silent.** An unreachable thread store leaves a usable
   *      empty chat scoped to the current context, never an error screen — the
   *      Assistant is a companion surface, and it must not take the page down
   *      with it.
   */
  useEffect(() => {
    const contextKey = contextKeyOf(resourceType, resourceId);
    if (resolvedContextRef.current === contextKey) return;

    /**
     * A navigation arriving mid-turn is DEFERRED, not discarded (task 780).
     *
     * This used to claim the new key and then skip resolving it, which was a
     * data-integrity bug rather than a cosmetic one: `threadId` stayed bound to
     * the previous resource's thread, and because the ref now equalled the new
     * key the guard above stopped this context from ever resolving again. Every
     * later send continued the wrong resource's thread — and since a thread
     * keeps its first home (the one-link guard from task 745), those turns could
     * never be re-filed. It healed only by leaving for a third context and
     * coming back.
     *
     * Returning BEFORE the claim leaves the pending context unresolved, so the
     * `isSending` dependency below re-fires this effect the moment the turn
     * lands and resolution runs for the page the User is actually on. The
     * in-flight reply is still never interrupted — it lands in its own thread,
     * and may render for a moment before the deferred resolution clears the
     * view, which is the honest trade against stranding it.
     */
    if (isSendingRef.current) return;

    const previousKey = resolvedContextRef.current;
    resolvedContextRef.current = contextKey;

    // The previous context's conversation belongs to the page the User just
    // left, so it clears immediately rather than lingering under the new one
    // until the fetch answers. This is also what leaves an empty chat behind
    // when the listing fails.
    if (previousKey !== null) {
      setTurns([]);
      setThreadId(null);
      bindThreadResource(null);
      setError(null);
      turnCountRef.current = 0;
    }

    let cancelled = false;
    /**
     * Whether this run got as far as putting its listing on screen. Until it
     * does, its claim on the context is provisional: a run cancelled before
     * delivering must leave the context UNRESOLVED, so the next invocation
     * resolves it rather than trusting a claim nobody honored (task 809).
     */
    let delivered = false;
    const generation = seedGenerationRef.current;
    setIsLoadingThreads(true);

    void (async () => {
      try {
        const listed = await threadsApi.list({
          // The scope this whole conversation lives on (task 805). A thread can
          // only be continued on the AI Service it belongs to, so a list drawn
          // from anywhere else would offer chats that 404 the moment they are
          // opened — which is exactly what put another endpoint's fixture thread
          // in this sidebar.
          endpointPath: ASSISTANT_ENDPOINT_PATH,
          ...(resourceType && resourceId ? { resourceType, resourceId } : { general: true }),
        });
        const rows = listed.data.data ?? [];
        if (cancelled) return;
        // From here this run has changed what is on screen, so the claim it
        // made at the top is now genuinely its own to keep.
        delivered = true;
        // The listing IS the history list: filtered server-side to this
        // context, newest first, so the open conversation is simply row one.
        setThreads(rows);

        const mostRecent = rows[0];
        if (!mostRecent || seedGenerationRef.current !== generation) return;

        const detail = await threadsApi.get(mostRecent.id);
        if (cancelled || seedGenerationRef.current !== generation) return;
        setThreadId(mostRecent.id);
        // The listing was filtered to this context, so this thread belongs to
        // it — that is the binding every later turn on it carries (task 808).
        bindThreadResource({ resourceType, resourceId });
        const messages = detail.data.data.messages ?? [];
        setTurns(turnsFromThread(messages));
        turnCountRef.current = messages.length;
      } catch {
        // Intentionally silent — see above.
      } finally {
        if (!cancelled) setIsLoadingThreads(false);
      }
    })();

    return () => {
      cancelled = true;
      /**
       * RELEASE AN UNHONORED CLAIM (task 809) — the invariant the deferred
       * -navigation comment above already promises in the other direction: a
       * resolution that did not land leaves the context unresolved.
       *
       * On a real navigation this is a no-op in effect: the run that owned the
       * previous context had delivered, and the run for the new context claims
       * it immediately afterwards. The case it exists for is a run cancelled
       * BEFORE delivering — StrictMode's double mount, or navigating away
       * mid-fetch — where holding the claim would tell the next invocation that
       * a context nobody resolved is already resolved.
       */
      if (!delivered) resolvedContextRef.current = previousKey;
    };
    // `isSending` is a dependency so a completing turn re-fires this and the
    // deferred navigation above finally resolves. Every other firing it causes
    // — including the flip to `true` at send start — is a no-op, because the
    // ref-equality guard at the top returns when nothing was deferred.
  }, [resourceId, resourceType, isSending, bindThreadResource]);

  /**
   * WHICH ARM OF THE PLAN SURFACE THE USER IS IN (task 998).
   *
   * The spec card, the quick-reply chips and the build result card each sit
   * behind their own flag, so a proposal records all three states — that is
   * what lets the freeform arm be compared against the card arm rather than
   * merely assumed better. Held in a
   * ref for the same reason as the refs above: `appendReply` is created once.
   */
  const specCardEnabled = useFlag('innovate.fe.assistant.specCard');
  const optionChipsEnabled = useFlag('innovate.fe.assistant.optionChips');
  const resultCardEnabled = useFlag('innovate.fe.assistant.resultCard');
  const planArmRef = useRef({
    specCard: specCardEnabled,
    optionChips: optionChipsEnabled,
    resultCard: resultCardEnabled,
  });
  planArmRef.current = {
    specCard: specCardEnabled,
    optionChips: optionChipsEnabled,
    resultCard: resultCardEnabled,
  };

  /** Fold a dispatcher reply into the stream. */
  const appendReply = useCallback((specialist: AssistantSpecialist, reply: AssistantReplyEnvelope | null, collaborationId?: string) => {
    if (!reply) {
      setTurns((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'assistant',
          text: "I didn't get a response that time — could you try rephrasing?",
        },
      ]);
      return;
    }

    const proposesAction = Boolean(reply.spec) && !reply.buildResult;
    if (proposesAction) {
      trackEvent('assistant_action_proposed', {
        specialist,
        location: currentLocationTag(),
        specCard: planArmRef.current.specCard,
        optionChips: planArmRef.current.optionChips,
        resultCard: planArmRef.current.resultCard,
      });
    }

    /**
     * APPROVAL BY TYPING (task 998) — the other half of `assistant_action_
     * approved`, which only ever fires from the card's button.
     *
     * The guide decides `phase: "building"` from the User's own reply, so a
     * build that arrives while the proposal it settles is still `proposed` was
     * approved in words: the button would have moved that turn to `approved`
     * before the request left. Read off the ref rather than inside the state
     * updater below, because an updater is a pure function React may call twice.
     */
    const lastProposal = [...turnsRef.current]
      .reverse()
      .find((turn) => turn.role === 'assistant' && turn.actionState);
    if (reply.phase === 'building' && lastProposal?.actionState === 'proposed') {
      trackEvent('assistant_action_typed_approval', {
        specialist,
        location: currentLocationTag(),
      });
    }

    if (reply.buildResult) {
      const succeeded = reply.buildResult.status === 'succeeded';
      trackEvent(succeeded ? 'assistant_action_completed' : 'assistant_action_failed', {
        specialist,
        location: currentLocationTag(),
      });
      if (succeeded) {
        // Same contract the Composer page emits (task 427): the build turn is
        // both the creation moment and the closest thing to publishing. A build
        // on the New AI Service page keeps `composer` as its surface (task
        // 1117): the page's builds have always reported it, and moving the page
        // onto this conversation must not end that series.
        const builtServiceId = reply.buildResult.resourceIds?.endpointId;
        trackEvent('ai_service_created', {
          service_id: builtServiceId,
          service_type: isAssistantPagePath(pathnameRef.current) ? 'composer' : 'assistant',
          conversational: reply.buildResult.conversational === true,
        });
        trackEvent('ai_service_published', { service_id: builtServiceId });
      }
    }

    setTurns((prev) => {
      // The run behind this reply (task 1151). A thread's turns all run on one
      // Collaboration, so this reply's turn is the count of earlier replies on
      // it — countable only when every earlier reply's run is known (none was
      // replayed from the transcript); otherwise the link opens the run on its
      // latest turn.
      const priorReplies = prev.filter((turn) => turn.role === 'assistant');
      const run = collaborationId
        ? {
            collaborationId,
            ...(priorReplies.every((turn) => turn.run)
              ? { turnIndex: priorReplies.filter((turn) => turn.run?.collaborationId === collaborationId).length }
              : {}),
          }
        : undefined;

      // The build turn resolves whichever card was awaiting application.
      const settled = reply.buildResult
        ? prev.map((turn) =>
            turn.actionState === 'approved'
              ? {
                  ...turn,
                  actionState:
                    reply.buildResult?.status === 'succeeded'
                      ? ('completed' as const)
                      : ('failed' as const),
                }
              : turn
          )
        : prev;

      return [
        ...settled,
        {
          id: newId(),
          role: 'assistant',
          text: replyText(reply),
          specialist,
          sources: reply.sources,
          answered: reply.answered,
          // Asking turns carry the guide's chips. A build turn carries the
          // builder's web-page offer chips (task 1137), which
          // `replyFromTurnOutput` lifts onto `reply.options`; the guide offers
          // no options while building, so on that turn they are the builder's.
          options: reply.phase === 'asking' || reply.buildResult ? reply.options : undefined,
          questions: reply.phase === 'asking' ? reply.questions : undefined,
          spec: reply.spec,
          actionState: proposesAction ? ('proposed' as const) : undefined,
          buildResult: reply.buildResult,
          // What the turn persisted server-side — the UI and test tiers save on
          // the data path, so the sidebar reports the reference rather than
          // owning a save of its own (tasks 763, 794).
          savedArtifact: reply.savedArtifact,
          // An unreadable generator reply (task 1121), so the card can say so.
          rawReply: reply.response,
          scenarios: reply.scenarios,
          // The files the turn produced, if any (task 828). Not tier-specific:
          // any definition can carry a save-artifact step.
          artifacts: reply.artifacts,
          // The connection the guide needs and the account has not set up
          // (task 962) — the structured half of a message that otherwise leaves
          // the User to find the vendor's endpoint themselves.
          missingConnection: reply.missingConnection,
          // The knowledge base the guide needs and the account has not set up
          // (task 1079), for the sidebar's Create knowledge base action.
          missingKnowledgeBase: reply.missingKnowledgeBase,
          run,
        },
      ];
    });

    // On the New AI Service page the reply lands in the page body, in view,
    // whatever state the docked pane was left in (task 1117).
    if (!isOpenRef.current && !isAssistantPagePath(pathnameRef.current)) setHasUnread(true);
  }, []);

  /**
   * Refresh what this finished turn SAVED (task 876).
   *
   * Separate from `routeCompletedTurn` on purpose, and called before it: the
   * cache is wrong whether or not the User can be navigated anywhere. Every
   * early return in the routing — not on a service page, on a different
   * service's page, already on the tab — is a case where the stale list still
   * needs correcting, and the reported one (already on the Interface tab) is the
   * case where the navigation does literally nothing.
   *
   * Keyed off the turn's `targetServiceId`, the same value the routing guards
   * on, so a result produced for one AI Service never refreshes another's cache.
   * A general turn carries no target and touches nothing.
   */
  const refreshSavedResult = useCallback(
    (
      specialist: AssistantSpecialist,
      reply: AssistantReplyEnvelope | null,
      targetServiceId?: string
    ) => {
      if (!reply || reply.savedArtifact?.saved !== true || !targetServiceId) return;
      const root = RESULT_QUERY_ROOTS[specialist];
      if (root) void queryClient.invalidateQueries({ queryKey: [root, targetServiceId] });
      const key = RESULT_QUERY_KEYS[specialist]?.(targetServiceId);
      if (key) void queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient]
  );

  /**
   * Refresh the AI SERVICES LIST after a build (task 878).
   *
   * The same class of problem as `refreshSavedResult` above, one level up: the
   * builder node creates the service INSIDE the turn, so the client runs no
   * create mutation and none of the `onSuccess` invalidations in `useEndpoints`
   * fire. A mounted list refetches only when something invalidates it, so a User
   * who builds while standing on the AI Services page — or the Dashboard, or any
   * page holding the list — keeps a list that does not contain the service the
   * "ready" card is describing, until a reload.
   *
   * Scoped by the BUILT service's own id rather than the turn's target, unlike
   * the refresh above: a create build has no target service at all, and it is
   * precisely the build that adds the row. The list key is a prefix — every
   * reader is `['endpoints', page, limit, filters]` (`useEndpoints`, and the
   * Dashboard's own query) — so the root invalidates all of them; the detail key
   * is `['endpoint', id]`, which matters on a modify build where the service page
   * the User is standing on is the stale one.
   */
  const refreshBuiltService = useCallback(
    (reply: AssistantReplyEnvelope | null) => {
      if (reply?.buildResult?.status !== 'succeeded') return;
      void queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      const builtServiceId = reply.buildResult.resourceIds?.endpointId;
      if (builtServiceId) {
        void queryClient.invalidateQueries({ queryKey: ['endpoint', builtServiceId] });
      }
    },
    [queryClient]
  );

  /**
   * Where this finished turn leaves the User (task 871) — the two rules stated
   * above `DASHBOARD_PATH`, applied in order.
   *
   * `targetServiceId` is the AI Service the TURN was about, which on a
   * continuation is the one the conversation is bound to rather than the one
   * the browser happens to be showing (task 808). It is what stops a result
   * produced for one service from reaching into another service's page when the
   * User moved while the reply was in flight.
   */
  const routeCompletedTurn = useCallback(
    (
      specialist: AssistantSpecialist,
      reply: AssistantReplyEnvelope | null,
      targetServiceId?: string
    ) => {
      if (!reply) return;
      const pathname = pathnameRef.current;

      // 1. The build landing. A failed build has nowhere to land.
      const builtServiceId =
        reply.buildResult?.status === 'succeeded'
          ? reply.buildResult.resourceIds?.endpointId
          : undefined;
      if (builtServiceId) {
        // The Dashboard and the AI Services list (task 1264): plain `navigate`,
        // so Back returns to the page the build was started from.
        if (isDashboardPath(pathname) || isServicesListPath(pathname)) {
          navigate(`/services/${builtServiceId}`);
        }
        // The New AI Service page is the second origin (task 1117). `replace`,
        // because the page the build left has done its job: Back from the new
        // service returns to wherever the User came to /services/new from,
        // not to a design conversation that now lives on the service.
        else if (isAssistantPagePath(pathname)) {
          navigate(`/services/${builtServiceId}`, { replace: true });
        }
        return;
      }

      // 2. The tab the saved result lives on. An unsaved one has nothing to
      // show there, and says so on the card instead.
      const tab = RESULT_TABS[specialist];
      if (!tab || reply.savedArtifact?.saved !== true) return;

      const serviceInView = serviceDetailIdFromPath(pathname);
      if (!serviceInView) return;
      if (targetServiceId && targetServiceId !== serviceInView) return;

      // `replace`, because selecting a tab is not a place in history — it is
      // the same rule the page's own tab row follows (`setSelectedTabIndex` in
      // `ServiceDetailPage`), and the switch is exactly what a click there
      // would do, so it can discard no state a click would keep.
      const section = RESULT_SECTIONS[specialist];
      navigate(`/services/${serviceInView}?tab=${tab}${section ? `&section=${section}` : ''}`, { replace: true });
    },
    [navigate]
  );

  /** The context-scoped listing — the one call every history refresh makes. */
  const listThreadsForContext = useCallback(
    () =>
      threadsApi.list({
        endpointPath: ASSISTANT_ENDPOINT_PATH,
        ...(resourceType && resourceId ? { resourceType, resourceId } : { general: true }),
      }),
    [resourceId, resourceType]
  );

  /**
   * Keep the history list truthful within a visit (task 776).
   *
   * `setThreads` used to run only at navigation resolution and in `newChat`, so
   * a chat STARTED in a fresh context never entered the list: the history
   * control — gated on the list being non-empty since task 772 — stayed hidden
   * until the User navigated away and back. This closes that, and with it the
   * second path to the same symptom: a navigation-time listing that failed
   * silently (task 746's contract) no longer mutes the controls for the whole
   * visit, because the next send relists.
   *
   * Refreshing the REAL listing rather than synthesizing a row is deliberate:
   * the server owns a thread's title and `lastActiveAt`, so an invented row
   * would show "Untitled chat" and then flip — a visible flicker in the exact
   * panel this exists to populate.
   *
   * Guarded to the context it was fired for, mirroring the resolution effect's
   * `cancelled` pattern: a response landing after the User has navigated away
   * belongs to a context that is no longer on screen, and writing it into
   * `threads` would show one context's history under another's conversation.
   *
   * **Interaction with deferred navigation (task 780).** When a turn completes
   * with a navigation deferred behind it, this fires from a closure that
   * captured the OLD context, while `resolvedContextRef` also still holds the
   * old key — so both orderings are safe:
   *
   *   - lands FIRST: the guard matches, the old context's rows are written, and
   *     the deferred resolution then claims the new key and overwrites them
   *     with its own listing. Transiently wrong, settled correct.
   *   - lands LAST: the ref now holds the new key, so it is discarded — which
   *     is harmless precisely because the deferred resolution lists for itself
   *     rather than relying on this refresh.
   */
  const refreshThreadsForContext = useCallback(async () => {
    const firedFor = contextKeyOf(resourceType, resourceId);
    try {
      const listed = await listThreadsForContext();
      if (resolvedContextRef.current !== firedFor) return;
      setThreads(listed.data.data ?? []);
    } catch {
      // History is a convenience; a failed refresh keeps the previous list and
      // the next send tries again. Never an error screen.
    }
  }, [listThreadsForContext, resourceId, resourceType]);

  const postTurn = useCallback(
    async (text: string, options?: { startsNewThread?: boolean }) => {
      // Pre-flight token refresh, the Composer precedent (task 395): a
      // composer-delegated turn forwards a token SNAPSHOT to the MCP server for
      // the whole run, and the build turn is the long one. Deliberately
      // non-fatal — a failed refresh should not block a turn that may well
      // succeed.
      try {
        await refreshToken?.();
      } catch {
        // Intentionally ignored — see above.
      }

      /**
       * EVERY turn states its thread intent explicitly (task 783): exactly one
       * of `threadId` (continue this conversation) or `newThread: true` (start
       * one).
       *
       * The third shape — neither key — used to leave here whenever the app had
       * no thread for the current context, and it is the one shape the app must
       * never send. The service answers an identity-only turn with that
       * identity's most recent ACTIVE thread on the endpoint, and the Assistant
       * is ONE endpoint multiplexing every navigation context, so the answer
       * routinely came from a different page's conversation — which the app then
       * adopted from the reply, cementing the wrong binding. The app always
       * knows which of the two it wants, so it says so.
       *
       * `startsNewThread` stays an explicit option for a caller that wants a
       * fresh thread while one is open; a null `threadId` implies it, which is
       * what makes New chat and a first turn on a fresh context work.
       *
       * Accepted trade-off: after a navigation-time listing that failed silently
       * (task 746's contract) on a context that DOES have a thread, this mints a
       * fresh one instead of gluing the turn to a possibly-wrong thread. A stray
       * extra thread beats cross-context contamination, which is unrecoverable —
       * a thread keeps its first home (task 745's one-link guard).
       */
      const continuing = !options?.startsNewThread && threadId;
      const threadTarget = continuing ? { threadId } : { newThread: true as const };

      /**
       * TARGET STICKINESS (task 808). A continued turn carries the resource the
       * THREAD belongs to, not the one the current route happens to name. The
       * page tag still comes from the route — that is honestly where the User
       * is — but the resource is conversation state, and a held modify
       * conversation must not lose its target because one turn was sent from
       * somewhere else, or from a moment when the route had not resolved.
       *
       * A new thread has no binding yet, so its first turn uses the route,
       * which is what files it under the right resource in the first place.
       */
      const boundContext =
        continuing && threadResourceRef.current
          ? { ...context, ...threadResourceRef.current }
          : context;

      /**
       * NO SERVICE OF ITS OWN, BUT THIS CONVERSATION BUILT ONE (task 1264). A
       * continued turn that carries no AI Service carries the one the thread
       * built, so the Interface, Test and Criteria saves attach to it rather
       * than refusing. A new thread never inherits it.
       */
      const turnContext =
        continuing && builtServiceIdForThread && boundContext.resourceType !== 'endpoint'
          ? { ...boundContext, resourceType: 'endpoint', resourceId: builtServiceIdForThread }
          : boundContext;

      /**
       * The target's current state travels with the resource that identifies it
       * (task 813). Built for the resource the TURN carries — the bound one on a
       * continuation — so the summary and the `resourceId` beside it always
       * describe the same service, and a general turn carries neither field.
       */
      const targetFields = assistantTargetFields({
        resourceType: turnContext.resourceType,
        resourceId: turnContext.resourceId,
        endpoint: targetEndpoint,
        generatedUis: targetGeneratedUis?.data,
        apiBaseUrl: config.API_URL,
        agentKnowledge: targetAgentKnowledge,
      });

      /**
       * AN EXPLICIT REVISION WINS THE `baseArtifact` KEY (task 828).
       *
       * There is one such key in the envelope and two things that can fill it:
       * the target's saved interface, which `assistantTargetFields` supplies on
       * every resource-bound turn, and a file the User pointed at and said
       * "revise this". The second is an act, not context — it happened because
       * someone clicked Revise on that card — so it takes precedence for the one
       * turn it was asked for.
       */
      const revising = revisingArtifactRef.current;
      const baseFields = revising
        ? {
            ...targetFields,
            baseArtifact: {
              artifactId: revising.id,
              versionNumber: revising.versionNumber,
              name: revising.name,
            },
          }
        : targetFields;

      /** The attached document this turn may carry — see the body below. */
      const attachedDocumentId =
        attachmentRef.current?.status === 'ready' ? attachmentRef.current.id : undefined;

      // The service module speaks the standard invoke path and unwraps its
      // envelope, so what arrives here is the same turn shape as before.
      const data = await assistantApi.sendMessage({
        message: text,
        context: turnContext,
        ...baseFields,
        // What this account has actually connected (task 961). The Composer
        // guide holds no tools, so without this it can only offer the two
        // platform connections named in its prompt. Read through the ref so a
        // list that resolves mid-conversation is picked up on the next turn
        // without re-creating this callback.
        ...(availableConnectionsRef.current.length > 0
          ? { availableConnections: availableConnectionsRef.current }
          : {}),
        // What knowledge bases this account has (task 1079), so the Composer
        // guide can name one when the User asks for a service that answers from
        // their own material. Same ref and same empty-means-absent rule.
        ...(availableKnowledgeBasesRef.current.length > 0
          ? { availableKnowledgeBases: availableKnowledgeBasesRef.current }
          : {}),
        // The attached document, on EVERY turn while the chip is up (task 970).
        // The engine holds the id for one turn only, so a conversation that keeps
        // working on the file keeps sending it.
        //
        // Only when it is READY: the service answers 422 for a document that is
        // still indexing, and the composer already holds Send until then — this
        // is the second half of the same rule, stated where the body is built so
        // no other caller of `postTurn` (the approval path sends "Yes, build it."
        // without touching the composer) can send an unusable id.
        ...(attachedDocumentId ? { documentId: attachedDocumentId } : {}),
        ...threadTarget,
      });

      // The first turn of this attachment's life that actually carried it (task
      // 970). Fired after the call rather than before it, so a turn that never
      // reached the service is not counted as a use of the document.
      if (attachedDocumentId && attachmentTurnTrackedRef.current !== attachedDocumentId) {
        attachmentTurnTrackedRef.current = attachedDocumentId;
        trackEvent('assistant_document_turn_sent', { location: currentLocationTag() });
      }

      // Spent: the base applied to the turn that was just sent, and the next one
      // starts from whatever the User points at next.
      if (revising) setRevisingArtifact(null);

      if (data.threadId) {
        setThreadId(data.threadId);
        // A thread minted by THIS turn is bound to the context that turn
        // carried, so the next one continues in the same place.
        if (!continuing) {
          bindThreadResource({
            resourceType: turnContext.resourceType,
            resourceId: turnContext.resourceId,
          });
        }
      }
      appendReply(data.specialist, data.reply, data.collaborationId);

      /**
       * The AI Service this turn was ABOUT — the bound one on a continuation
       * (task 808), not whatever the browser happens to be showing. Both the
       * refresh and the routing below are scoped by it.
       */
      const targetServiceId =
        turnContext.resourceType === 'endpoint' ? turnContext.resourceId : undefined;

      // What the turn saved server-side, refreshed in the cache the tab reads
      // (task 876). Before the routing, because the routing may do nothing at
      // all — including in the one case the User is most likely to notice.
      refreshSavedResult(data.specialist, data.reply, targetServiceId);

      // What the turn BUILT, refreshed in the list every page reads (task 878).
      // Beside the refresh above and for the same reason: the cache is wrong
      // regardless of where the routing below decides to leave the User.
      refreshBuiltService(data.reply);

      // Remember what this thread built (task 1264), for the turns after it.
      const builtEndpointId =
        data.reply?.buildResult?.status === 'succeeded'
          ? data.reply.buildResult.resourceIds?.endpointId
          : undefined;
      if (builtEndpointId && data.threadId) {
        setBuiltService({ threadId: data.threadId, endpointId: builtEndpointId });
      }

      // Where the finished turn leaves the User (task 871). After the reply is
      // on screen, so the card the User is being carried away from — or towards
      // — has rendered either way.
      routeCompletedTurn(data.specialist, data.reply, targetServiceId);

      /**
       * TWO TRIGGERS RELIST THE HISTORY, and both are about the row's NAME.
       *
       * 1. A thread the list does not know: either the first turn of a new chat,
       *    or a list that failed to load.
       * 2. A thread the list holds with NO TITLE (task 875). The server owns the
       *    name — task 874 names an untitled thread during the turn — so the row
       *    still reading "Untitled chat" is exactly the row whose name has just
       *    changed underneath the list. Without this it kept that placeholder
       *    until the User navigated away and back.
       *
       * Both read through a ref so this callback is not re-created on every
       * listing. A thread already in the list WITH a title needs nothing: a
       * continuation moves only `lastActiveAt`, which no visible control
       * depends on.
       */
      const listedThread = data.threadId
        ? threadsRef.current.find((thread) => thread.id === data.threadId)
        : undefined;
      const isUnknownThread = Boolean(data.threadId) && !listedThread;
      const isUntitledThread = Boolean(listedThread) && !listedThread?.title?.trim();
      if (isUnknownThread || isUntitledThread) {
        /**
         * Deliberately NOT awaited: awaiting would hold `isSending` true until
         * the listing answered, and an in-flight turn suppresses navigation
         * resolution (task 746) — so a slow relist would freeze the User's next
         * move on a call that only populates a control. Firing it loose is also
         * what makes the context guard inside it matter rather than be
         * theoretical.
         */
        void refreshThreadsForContext();
      }
    },
    [
      appendReply,
      bindThreadResource,
      builtServiceIdForThread,
      context,
      refreshBuiltService,
      refreshSavedResult,
      refreshThreadsForContext,
      refreshToken,
      routeCompletedTurn,
      targetAgentKnowledge,
      targetEndpoint,
      targetGeneratedUis,
      threadId,
    ]
  );

  const send = useCallback(
    async (text: string, options?: { startsNewThread?: boolean }) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;

      // `help_assistant_asked` fires at SUBMISSION, not on the answer — the
      // task-541 contract this surface inherits from the modal panel, kept
      // byte-identical so the metric survives the container change. The
      // question text never leaves the browser.
      turnCountRef.current += 1;
      trackEvent('help_assistant_asked', {
        location: currentLocationTag(),
        turn: turnCountRef.current,
      });

      setTurns((prev) => [...prev, { id: newId(), role: 'human', text: trimmed }]);
      setIsSending(true);
      setError(null);

      try {
        await postTurn(trimmed, options);
      } catch (err) {
        const assistantError = toComposerError(err);
        setError(
          hasFriendlyMessage(assistantError)
            ? assistantError.message
            : `Something went wrong reaching Innovate: ${assistantError.message || 'Unknown error'}`
        );
      } finally {
        setIsSending(false);
      }
    },
    [isSending, postTurn]
  );

  /**
   * Approve a proposed action. Approval is a turn in the conversation, not a
   * side channel: the specialist that proposed it is the one that applies it,
   * running as the User through the same APIs the app uses.
   */
  const approveAction = useCallback(
    async (turnId: string) => {
      if (isSending) return;
      const turn = turns.find((entry) => entry.id === turnId);
      if (!turn || turn.actionState !== 'proposed') return;

      trackEvent('assistant_action_approved', {
        specialist: turn.specialist,
        location: currentLocationTag(),
      });

      setTurns((prev) =>
        prev.map((entry) =>
          entry.id === turnId ? { ...entry, actionState: 'approved' as const } : entry
        )
      );
      setIsSending(true);
      setError(null);

      try {
        await postTurn('Yes, build it.');
      } catch (err) {
        const assistantError = toComposerError(err);
        trackEvent('assistant_action_failed', {
          specialist: turn.specialist,
          location: currentLocationTag(),
        });
        setTurns((prev) =>
          prev.map((entry) =>
            entry.id === turnId ? { ...entry, actionState: 'failed' as const } : entry
          )
        );
        setError(
          hasFriendlyMessage(assistantError)
            ? assistantError.message
            : `The build could not be started: ${assistantError.message || 'Unknown error'}`
        );
      } finally {
        setIsSending(false);
      }
    },
    [isSending, postTurn, turns]
  );

  /**
   * New chat. The old thread stays retrievable, so this clears the view rather
   * than the record: clearing `threadId` is what makes the next turn carry
   * `newThread: true` (task 783 — a turn with no thread to continue says so
   * explicitly), and the server mints the fresh thread from that.
   *
   * The fresh chat starts in the CURRENT context (task 746) — on a resource page
   * its first turn carries that resource in the envelope, which is what files
   * the new thread under it — so the history refresh reads the same scope.
   */
  const newChat = useCallback(() => {
    setTurns([]);
    setThreadId(null);
    // A chat that does not exist yet has no home; its first turn decides one.
    bindThreadResource(null);
    setError(null);
    turnCountRef.current = 0;
    // Same guarded listing the post-send refresh uses (task 776), so the two
    // paths into the history list cannot drift apart.
    void refreshThreadsForContext();
  }, [bindThreadResource, refreshThreadsForContext]);

  /**
   * The New AI Service page's first turn (task 1117).
   *
   * New chat and a send in one step: the view clears, the resolution race guard
   * moves on, and the description goes out as the first turn of a NEW thread
   * (`startsNewThread`, so `newThread: true` on the wire). `/services/new` is a
   * general page, so the thread starts general; a successful build re-files it
   * under the service it built, which is where the landing rule takes the User.
   */
  const seedFirstTurn = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || isSending) return;
      seedGenerationRef.current += 1;
      setTurns([]);
      setThreadId(null);
      bindThreadResource(null);
      setError(null);
      turnCountRef.current = 0;
      await send(trimmed, { startsNewThread: true });
    },
    [bindThreadResource, isSending, send]
  );

  const resumeThread = useCallback(async (id: string) => {
    setError(null);
    try {
      const detail = await threadsApi.get(id);
      setThreadId(id);
      // History is listed per context (task 746), so a resumed row belongs to
      // the context it was listed under — the same binding a resolved thread
      // gets, so resuming and navigating cannot disagree (task 808).
      bindThreadResource({ resourceType, resourceId });
      setTurns(turnsFromThread(detail.data.data.messages ?? []));
      turnCountRef.current = detail.data.data.messages?.length ?? 0;
    } catch {
      setError('That chat could not be opened. Please try again.');
    }
  }, [bindThreadResource, resourceType, resourceId]);

  const renameThread = useCallback(async (id: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      const res = await threadsApi.rename(id, trimmed);
      const savedTitle = res.data.data.title ?? trimmed;
      setThreads((prev) =>
        prev.map((thread) => (thread.id === id ? { ...thread, title: savedTitle } : thread))
      );
    } catch {
      setError('That chat could not be renamed. Please try again.');
    }
  }, []);

  /**
   * Archive a thread (task 777). The record survives — the server flips it to
   * `archived` and stops listing it — so archiving the OPEN chat behaves like
   * New chat: the view clears rather than the conversation being destroyed.
   */
  const archiveThread = useCallback(
    async (id: string) => {
      try {
        await threadsApi.archive(id);
        setThreads((prev) => prev.filter((thread) => thread.id !== id));
        if (threadId === id) newChat();
      } catch {
        setError('That chat could not be archived. Please try again.');
      }
    },
    [newChat, threadId]
  );

  const open = useCallback(() => {
    setIsOpen(true);
    setHasUnread(false);
  }, []);

  /**
   * THE COMPOSER DRAFT (task 1131). The Interface tab's "Create an interface"
   * hands the User a sentence to finish rather than a turn already sent: what
   * they want the interface to look like is the part only they know. The same
   * reasoning as the composer's own "Revise" seeding (task 828), reachable from
   * outside the sidebar.
   */
  const [draft, setDraftState] = useState<AssistantDraft | null>(null);
  const draftNonceRef = useRef(0);
  const setDraft = useCallback((text: string) => {
    draftNonceRef.current += 1;
    setDraftState({ text, nonce: draftNonceRef.current });
  }, []);
  const openWithDraft = useCallback(
    (text: string) => {
      open();
      setDraft(text);
    },
    [open, setDraft]
  );
  const consumeDraft = useCallback(() => setDraftState(null), []);

  /** THE COMPOSER PULSE (task 1196) — see `composerPulse` on the context. */
  const [composerPulse, setComposerPulse] = useState<number | null>(null);
  const composerPulseNonceRef = useRef(0);
  const pulseComposer = useCallback(() => {
    composerPulseNonceRef.current += 1;
    setComposerPulse(composerPulseNonceRef.current);
  }, []);
  const consumeComposerPulse = useCallback(() => setComposerPulse(null), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) setHasUnread(false);
      return !prev;
    });
  }, []);

  const presentation: AssistantPresentation = isAssistantPagePath(location.pathname)
    ? 'page'
    : viewportPresentation;

  const value = useMemo<AssistantContextValue>(
    () => ({
      turns,
      threadId,
      isSending,
      error,
      context,
      threads,
      isLoadingThreads,
      send,
      approveAction,
      revisingArtifact,
      reviseArtifact,
      attachment,
      setAttachment,
      clearAttachment,
      newChat,
      seedFirstTurn,
      draft,
      setDraft,
      openWithDraft,
      consumeDraft,
      composerPulse,
      pulseComposer,
      consumeComposerPulse,
      resumeThread,
      renameThread,
      archiveThread,
      presentation,
      isOpen,
      open,
      close,
      toggle,
      hasUnread,
    }),
    [
      turns,
      threadId,
      isSending,
      error,
      context,
      threads,
      isLoadingThreads,
      send,
      approveAction,
      revisingArtifact,
      reviseArtifact,
      attachment,
      setAttachment,
      clearAttachment,
      newChat,
      seedFirstTurn,
      draft,
      setDraft,
      openWithDraft,
      consumeDraft,
      composerPulse,
      pulseComposer,
      consumeComposerPulse,
      resumeThread,
      renameThread,
      archiveThread,
      presentation,
      isOpen,
      open,
      close,
      toggle,
      hasUnread,
    ]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export default AssistantProvider;
